import React, { useRef, useMemo, useEffect } from 'react';
import {
  useForm,
  Form,
  Input,
  Edit,
  Col,
  Row,
  RefreshButton,
  ListButton,
  Space,
  TagField,
  TextField,
  Select,
  useModal,
  Card,
  DateField,
  Typography,
  MarkdownField,
  Timeline,
} from '@pankod/refine-antd';
import type { HttpError, OpenNotificationParams } from '@pankod/refine-core';
import { useMany } from '@pankod/refine-core';

import { MessageModal } from 'components';

import { LeadTypes, TimelineTypes } from 'interfaces';

import { LEAD_STATUS } from 'constant';
import { colorDecider, textDecider } from 'utils';

const { Title } = Typography;

const LeadEdit: React.FC = () => {
  const ref: any = useRef(null);
  const { modalProps, show, close } = useModal();
  const { formProps, saveButtonProps, queryResult } = useForm<
    LeadTypes,
    HttpError,
    LeadTypes
  >({
    metaData: {
      resource: 'leads',
      fields: [
        'id',
        'source',
        'name',
        'email',
        'phone',
        'country',
        'company',
        'message',
        'performed_by',
        'status',
      ],
    },
    errorNotification(error: any, variables, context): OpenNotificationParams {
      return {
        message: error?.response?.errors[0]?.message,
        type: 'error',
      };
    },
  });

  const { data, isLoading }: any = queryResult;
  const record = data?.data;

  const { data: timelimeData, isLoading: loading } = useMany<TimelineTypes>({
    resource: 'timelines',
    metaData: {
      fields: ['id', 'note', 'performed_by', 'action'],
    },
    ids: [record?.id],
    queryOptions: {
      enabled: record?.id ? true : false,
    },
  });

  const timelines: TimelineTypes[] = useMemo(
    () => timelimeData?.data ?? [],
    [timelimeData?.data]
  );

  useEffect(() => {
    ref?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [timelines]);

  return (
    <Edit
      isLoading={isLoading}
      saveButtonProps={saveButtonProps}
      headerProps={{
        extra: (
          <Space>
            <ListButton />
            <RefreshButton onClick={() => queryResult?.refetch()} />
          </Space>
        ),
      }}
    >
      <Row gutter={[30, 30]}>
        <Col xs={24} lg={14}>
          <Form
            {...formProps}
            layout='vertical'
            onFinish={(values) =>
              formProps.onFinish?.({
                ...values,
              } as any)
            }
          >
            <Row gutter={16}>
              <Col span={24} style={{ marginBottom: '20px' }}>
                <Row gutter={16}>
                  <Col span={24}>
                    <TextField strong value='Performed By' />{' '}
                    <TagField value={record?.performed_by} />
                  </Col>
                </Row>
              </Col>

              <Col span={8}>
                <Form.Item
                  label='Source'
                  name='source'
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label='Name'
                  name='name'
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label='Phone'
                  name='phone'
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label='Email'
                  name='email'
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item label='Country' name='country'>
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label='Company' name='company'>
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label='Message'
              name='message'
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input.TextArea rows={4} />
            </Form.Item>

            <Form.Item
              label='Status'
              name='status'
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                placeholder='Status'
                options={[
                  { label: 'New', value: LEAD_STATUS.NEW },
                  { label: 'In progress', value: LEAD_STATUS.IN_PROGRESS },
                  { label: 'Made Offer', value: LEAD_STATUS.MADE_OFFER },
                  {
                    label: 'Deal Closed',
                    value: LEAD_STATUS.DEAL_CLOSED_SUCCESS,
                  },
                  { label: 'Deal Failed', value: LEAD_STATUS.DEAL_FAILED },
                ]}
                onChange={show}
              />
            </Form.Item>

            <Form.Item label='Note' name='note'>
              <Input.TextArea rows={4} />
            </Form.Item>
          </Form>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            className='timeline-data'
            title='Timeline'
            style={{ height: '585px', overflowY: 'auto' }}
            bodyStyle={{ overflowY: 'auto' }}
            headStyle={{
              position: 'sticky',
              top: 0,
              backgroundColor: '#2a132e',
              color: 'white',
              zIndex: 1,
            }}
          >
            {loading ? (
              <Title>Loading....</Title>
            ) : (
              <React.Fragment>
                {timelines?.length > 0 ? (
                  <Timeline>
                    {timelines?.map((timeline: TimelineTypes) => {
                      return (
                        <Timeline.Item>
                          {textDecider(timeline?.action, timeline.performed_by)}{' '}
                          <TagField
                            value={timeline?.action}
                            color={colorDecider(timeline?.action)}
                          />
                          <DateField format='LLL' value={timeline?.createdAt} />
                          <MarkdownField value={`Note: ${timeline?.note}`} />
                        </Timeline.Item>
                      );
                    })}
                    <div ref={ref} />
                  </Timeline>
                ) : null}
              </React.Fragment>
            )}
          </Card>
        </Col>
      </Row>

      <MessageModal
        onClose={close}
        modalProps={modalProps}
        message='Make sure to update the note according to the new status'
      />
    </Edit>
  );
};

export default LeadEdit;
